'use client';

import { FC, useEffect, useContext, useState } from 'react';
import {
  Flex,
  Button,
  ButtonGroup,
  Input,
  Select,
  Toggle,
  Separator,
  EntityTypeSelector,
} from '@/_components';
import type { CreateEntityFormProps } from './CreateEntityForm.types';
import { COUNTRY_ITEMS } from '@/_constants/countries';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { buildEntityName } from '@/_utils/companies';
import { createCompany, type CreateCompanyAttrs } from '@/_api/admin-companies';
import { countries } from 'countries-list';

export const CreateEntityForm: FC<CreateEntityFormProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    loadEntityTypeCounts,
    selectedUser,
    selectedClient,
    setSelectedUser,
  } = useContext(CompanyContext);
  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);

  const [isDetailed, setDetailed] = useState<boolean>(false);

  const resetSelectedUser = () => setSelectedUser(undefined);
  const onDetailedToggle = () => setDetailed(!isDetailed);

  const [isLoading, setLoading] = useState<boolean>(false);
  const [entityType, setEntityType] = useState<string>('');

  const [entityName, setEntityName] = useState<string>('');
  const [businessEmail, setBusinessEmail] = useState<string>('');
  const [otherEmail, setOtherEmail] = useState<string>('');

  const onEntityTypeChange = (newEntityType: string) =>
    setEntityType(newEntityType);

  const onEntityNameChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setEntityName(event.target.value);
  const onBusinessEmailChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setBusinessEmail(event.target.value);
  const onOtherEmailChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setOtherEmail(event.target.value);

  const [streetAddress, setStreetAddress] = useState<string>('');
  const [country, setCountry] = useState<string>('');

  const onCountryChange = (event: React.ChangeEvent<HTMLSelectElement>) =>
    setCountry(event.target.value);
  const onStreetAddressChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setStreetAddress(event.target.value);

  const [city, setCity] = useState<string>('');
  const [zip, setZip] = useState<string>('');
  const [state, setState] = useState<string>('');

  const onCityChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setCity(event.target.value);
  const onZipChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setZip(event.target.value);
  const onStateChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setState(event.target.value);

  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');

  const onFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFirstName(event.target.value);
    setEntityName(buildEntityName(event.target.value, lastName));
  };

  const onLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLastName(event.target.value);
    setEntityName(buildEntityName(firstName, event.target.value));
  };

  const onSave = async () => {
    try {
      setLoading(true);

      if (entityName.includes('/')) {
        return addToast('error', 'Entity Name cannot contain a "/" character.');
      }

      const newCompany: CreateCompanyAttrs = {
        entity_type: entityType,
        ein: '',
        lastName: lastName || '',
        firstName: firstName || '',
        fax: '',
        name: entityName || '',
        business_email: businessEmail,
        other_email: otherEmail,
        street: streetAddress || null,
        country: country || null,
        city: city || null,
        zip: zip || null,
        state: state || null,
        user_id: selectedUser?.id || selectedClient?.id || -1,
      };

      const createdCompany = await createCompany({
        ...newCompany,
        token,
      });

      if (!!createdCompany?.error) {
        return addToast('error', null, { error: createdCompany?.error });
      }

      addToast('success', 'The entity was successfully created');

      loadEntityTypeCounts();
      onClose();
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setLoading(false);
      setFirstName('');
      setOtherEmail('');
      setLastName('');
      setEntityName('');
      setEntityType('');
      setZip('');
      setBusinessEmail('');
      setState('');
      setStreetAddress('');
      setCountry('');
      setCity('');
    }
  }, [isOpen]);

  const isValid: boolean = !!entityType && !!entityName;
  const isIndividual: boolean = entityType === 'Individual';

  return (
    <Flex isColumn={true} gap={10}>
      {/* Entity Type - Entity Name - Business Email */}
      <Flex gap={6}>
        <EntityTypeSelector value={entityType} onChange={onEntityTypeChange} />
        <Input
          label="Entity Name"
          placeholder="Entity Name"
          name="company-name"
          value={entityName}
          isRequired={true}
          isDisabled={isIndividual}
          onChange={onEntityNameChange}
        />
      </Flex>

      {isIndividual && (
        <Flex gap={6}>
          <Input
            label="First Name"
            value={firstName}
            placeholder="John"
            isDisabled={isLoading}
            onChange={onFirstNameChange}
          />
          <Input
            label="Last Name"
            value={lastName}
            placeholder="Doe"
            isDisabled={isLoading}
            onChange={onLastNameChange}
          />
        </Flex>
      )}

      {isDetailed && (
        <Flex gap={6}>
          <Input
            label="Entity email"
            placeholder="hello@example.com"
            value={businessEmail}
            isDisabled={isLoading}
            onChange={onBusinessEmailChange}
          />
          <Input
            label="Other Email"
            value={otherEmail}
            placeholder="other@example.com"
            isDisabled={isLoading}
            onChange={onOtherEmailChange}
          />
        </Flex>
      )}

      {/* Street Address */}

      {isDetailed && (
        <Flex gap={6}>
          <Input
            label="Street Address"
            placeholder="e.g., Oak Street"
            value={streetAddress}
            isDisabled={isLoading}
            onChange={onStreetAddressChange}
          />
          <Select
            items={COUNTRY_ITEMS}
            label="Country"
            onChange={onCountryChange}
            isDisabled={isLoading}
            selectedValue={country in countries ? country : ''}
          />
        </Flex>
      )}

      {isDetailed && (
        <Flex gap={4}>
          <Input
            label="City"
            placeholder="e.g., San Francisco"
            value={city}
            isDisabled={isLoading}
            onChange={onCityChange}
          />
          <Input
            label={country === 'CA' ? 'Province/Territory' : 'State'}
            placeholder={country === 'CA' ? 'e.g., BC' : 'e.g., CA'}
            value={state}
            isDisabled={isLoading}
            onChange={onStateChange}
          />
          <Input
            label={country === 'CA' ? 'Postal Code' : 'Zip Code'}
            placeholder={country === 'CA' ? 'e.g., V5T 1B3' : 'e.g., 94120'}
            value={zip}
            isDisabled={isLoading}
            onChange={onZipChange}
          />
        </Flex>
      )}
      <Separator isLight={true} />
      <Flex isBetween={true} gap={2}>
        <Toggle
          isChecked={isDetailed}
          onChange={onDetailedToggle}
          label="Add additional details"
        />
        <ButtonGroup align="right">
          <Button
            intent="secondary"
            value="Switch selected user"
            onClick={resetSelectedUser}
          />
          <Button
            intent="primary"
            value="Create entity"
            onClick={onSave}
            isDisabled={isLoading || !isValid}
            isLoading={isLoading}
          />
        </ButtonGroup>
      </Flex>
    </Flex>
  );
};
